import React, { useState, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import uuid from 'uuid/v1';
import {
  Icon,
  Icons,
  Tooltip,
  TextField,
  InputAdornment,
  styled,
  Button,
  Grid,
  Typography,
} from '@nextiva/next-ui-components';

export const StyledSearchTextField = styled(TextField)`
  & .MuiInputBase-input::placeholder {
    color: #d9dbec !important;
  }
`;
export const StyledSearchIcon = styled(Icon)`
  width: 14px;
  height: 14px;
  color: ${({ theme }) => theme.palette.blueGrey.A300};
`;
export const StyledClearIcon = styled(Icon)`
  cursor: default;
  height: 16.67px;
  width: 16.67px;
`;

const StyledButton = styled(Button)`
  flex: 1;
`;

const StyledTypography = styled(Typography)`
  color: #232649;
  font-weight: 900;
  line-height: 36px;
  letter-spacing: 0.75px;
`;
const StyledSvgBox = styled('div')`
  position: relative;
  & > svg {
    position: absolute;
    top: 12px;
    left: 45%;
    cursor: pointer;
    fill: ${({ theme }) => theme.palette.blueGrey.A400};
  }
`;

const StyledDividerSpan = styled('span')`
  margin: 0 15px;
  padding-bottom: 24px;
  border-left: 1px solid ${({ theme }) => theme.palette.common.black};
  opacity: 0.25;
`;
const SearchBar = ({
  label = '',
  placeholder = '',
  id = uuid(),
  searchData = () => {},
  actionName = '',
  onClick = () => {},
  disabled = false,
  buttonDisabled = false,
  downloadCSV,
  'data-testid': dataTestId,
}) => {
  const [val, setValue] = useState('');

  const onChangeHandler = useCallback(
    (e) => {
      setValue(e.target.value?.trim());
      searchData(e.target.value?.trim());
    },
    [setValue, searchData],
  );

  const clearHandler = () => {
    setValue('');
    searchData('');
  };

  return (
    <Grid container spacing={0}>
      <Grid item xs={4}>
        <StyledTypography
          variant="h5"
          data-testid="PlatformAdminUI-SearchBar-Header"
        >
          {label}
        </StyledTypography>
      </Grid>
      <Grid item xs={1}>
        <StyledSvgBox>
          <Tooltip placement="bottom" title="Download CSV File">
            <Icon
              data-testid="PlatformAdminUI-ManageUsers-SearchBar-DownloadIcon"
              icon={Icons.download}
              size={24}
              role="button"
              onClick={downloadCSV}
            />
          </Tooltip>
        </StyledSvgBox>
      </Grid>
      <Grid
        item
        xs={4}
        align="right"
        data-testid="PlatformAdminUI-SearchBar-SearchField"
      >
        <StyledSearchTextField
          hiddenLabel
          id={id}
          value={val}
          fullWidth
          placeholder={placeholder}
          onChange={onChangeHandler}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <StyledSearchIcon icon={Icons.search} />
              </InputAdornment>
            ),
            endAdornment: val && (
              <InputAdornment position="end">
                <StyledClearIcon
                  icon={Icons.close}
                  onClick={clearHandler}
                  data-testid={dataTestId}
                />
              </InputAdornment>
            ),
          }}
          data-testid={dataTestId}
          disabled={disabled}
        />
      </Grid>

      <Grid item container xs={3}>
        <StyledDividerSpan />
        <StyledButton
          id="searchbarButton"
          disabled={buttonDisabled || disabled}
          color="primary"
          size="medium"
          variant="contained"
          onClick={onClick}
          data-testid="PlatformAdminUI-SearchBar-CreateButton"
        >
          {actionName}
        </StyledButton>
      </Grid>
    </Grid>
  );
};

SearchBar.propTypes = {
  actionName: PropTypes.string,
  buttonDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  downloadCSV: PropTypes.func,
  id: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  searchData: PropTypes.func,
  onClick: PropTypes.func,
};

export default React.memo(SearchBar);
